/**
 * Home component
 * Modified on : 17-04-2025
 */
//  Generic imports
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Outlet, useLocation } from "react-router-dom";

/**
 * Motif imports
 */
import { MotifButton, MotifFormField, MotifTextLink } from "@ey-xd/motif-react";
import MotifTabNavigation, {
  MotifTabControl,
} from "@ey-xd/motif-react/TabNavigation";
import MotifToast from "@ey-xd/motif-react/Toast";
import MotifSearch from "@ey-xd/motif-react/Search";

//  Icons imports
import iconRefresh from "../../../assets/images/refresh.svg";
import iconCheckCircle from "../../../assets/images/check-circle.svg";
import iconSearch from "../../../assets/images/search.svg";
import iconWarning from "../../../assets/images/warning-triangle.svg";
import iconProgressLoaderCircle from "../../../assets/images/Loading_indicator_circles.svg";

//  Redux imports
import {
  setUserProjects,
  setPlatformAdminUsers,
  setSearchProjectValue,
} from "../../../redux/userSlice";
import { setTableTypes } from "../../../redux/promptSlice";
/**
 * Internal imports
 */
import "./Home.scss";
import { homeConfig } from "../../../config/homeConfig";
import ProjectService from "../../../services/projectService";
import PromptLibraryService from '../../../services/promptLibraryService';
import PaansService from "../../../services/paansService";
import GraphService from "../../../services/graphService";
import PaansAcknowledge from "../paansAcknowledge/paansAcknowledge";

export default function Home() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const projectService = new ProjectService();
  const promptLibraryService = new PromptLibraryService();
  const [activeTab, setActiveTab] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [listOfProjects, setListOfProjects] = useState(false);
  const [loadingInProgress, setLoadingInProgress] = useState(false);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(null);
  const [showToastMessage, setShowToastMessage] = useState(false);
  const [paansAcknowledge, setPaansAcknowledge] = useState(true);
  const [showPaansToastMessage, setShowPaansToastMessage] = useState(false);
  const isPageRendered = useRef(false);
  const { user } = useSelector((state) => state.user);

  const privacyNoticeUrl = process.env.REACT_APP_PRIVACY_NOTICE;
  const engagementGuidanceUrl = process.env.REACT_APP_ENGAGEMENT_GUIDANCE;

  /**
   * Function will trigger to check the PAANS acknowledgment from Page load and Refersh button
   * @param {*} refresh
   */
  const getPaansAcknowledgeStatus = async (refresh) => {
    setShowRefreshButton(true);
    const paansService = new PaansService();
    const paansResponse = await paansService.getPaansAcknowledgeStatus(
      user?.username
    );
    setPaansAcknowledge(paansResponse?.hasAccepted);
    if (paansResponse && !paansResponse.hasAccepted && isPageRendered.current) {
      setShowPaansToastMessage(true);
    } else {
      await getProjects();
      if (refresh) {
        setShowToastMessage(true);
        setShowPaansToastMessage(false);
      }
    }
    isPageRendered.current = true;
  };

  useEffect(() => {
    setLoadingInProgress(true);
    getPlatformAdminList();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user) {
      getPaansAcknowledgeStatus();
    }
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    const asyncGetProjects = async () => {
      if (paansAcknowledge) {
        const pathname = window.location.pathname;
        if (pathname === "/home/projects") {
          setActiveTab("projectsTab");
          document.getElementById("projectsTab").click();
          setShowRefreshButton(true);
        } else if (pathname === "/home/promptLibrary") {
          setActiveTab("promptLibraryTab");
          document.getElementById("promptLibraryTab").click();
        }
        setShowPaansToastMessage(false);
      }
    };
    asyncGetProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paansAcknowledge]);

  /**
   * Retrieves the platform admin list by access token.
   */
  async function getPlatformAdminList() {
    const graphService = new GraphService();
    const platformAdminUsers = await graphService.getPlatformAdminList();
    if (platformAdminUsers) {
      dispatch(
        setPlatformAdminUsers({
          platformAdminUsers: platformAdminUsers,
        })
      );
    }
  }

  const filterProjects = async (e) => {
    setSearchValue(e.target.value);
    dispatch(setSearchProjectValue({ searchProjectValue: e.target.value }));
    let filteredProjectlist = listOfProjects.filter((item) => {
      return item.projectName
        .toLowerCase()
        .trim()
        .includes(e.target.value.trim().toLowerCase());
    });
    if (filteredProjectlist.length > 0) {
      dispatch(setUserProjects({ projects: filteredProjectlist }));
    } else {
      dispatch(setUserProjects({ projects: [] }));
    }
    setLoadingInProgress(false);
    setRefreshStatus("success");
  };

  useEffect(() => {
    const getTableTypes = async () => {
      const vData = {
        ProjectScopeId: '',
        Type: 'A',
        application: 'EXL_AddIn',
        PromptType: 'Central',
      };
      const vTableTypes = await promptLibraryService.getTableTypes(vData);
      if (!vTableTypes || !Array.isArray(vTableTypes)) return;

      const formattedPrompts = vTableTypes.map((prompt, index) => ({
        ...prompt,
        id: prompt?.id || `generated-${index}`,
        name: prompt.promptName || 'Unnamed Prompt',
        tableType: prompt.api || 'Unknown Type',
        isPublished: prompt.isPublished 
      }));
      dispatch(setTableTypes({ tableTypes: formattedPrompts }));
    };
    getTableTypes();
    //  eslint-disable-next-line
  }, [dispatch]);

  const getProjects = async () => {
    try {
      setLoadingInProgress(true);
      const projects = await projectService.GetProjectMasterData(
        user?.username,
        []
      );
      if (projects) {
        setListOfProjects(projects);
        dispatch(
          setUserProjects({
            projects: projects,
          })
        );
      }
      setLoadingInProgress(false);
      setRefreshStatus("success");
      //  eslint-disable-next-line
    } catch (e) {
      setLoadingInProgress(false);
      setRefreshStatus("error");
    }
  };

  const handleTabClick = (tab) => {
    const bodySectionLayer = document.getElementsByClassName("body-section")[0];
    switch (tab) {
      case "Projects":
        setActiveTab("projectsTab");
        setShowRefreshButton(true);
        navigate("projects");
        if (bodySectionLayer) {
          bodySectionLayer.style.height = "revert-layer";
        }
        break;
      case "PromptLibrary":
        if (activeTab !== "promptLibraryTab") {
          setActiveTab("promptLibraryTab");
          setShowRefreshButton(false);
          navigate("promptLibrary");
          if (bodySectionLayer) {
            bodySectionLayer.style.height = "auto";
          }
        }
        break;
      default:
    }
  };

  /**
   * Function will handle refresh of PAANS and projects
   * @param {*} refresh
   */
  const refreshProjectsList = async () => {
    setLoadingInProgress(true);
    getPaansAcknowledgeStatus(true);
  };

  return (
    <React.Fragment>
      {paansAcknowledge && (
        <>
          <div className="tab-section">
            <MotifTabNavigation className={"tab-nav"} variant="alt">
              <MotifTabControl
                id="projectsTab"
                onClick={() => handleTabClick("Projects")}
                tabIndex={0}
                role="tab"
              >
                <span
                  className={
                    activeTab === "projectsTab"
                      ? "tab-button tab-active"
                      : "tab-button"
                  }
                >
                  {homeConfig?.tabLabelProjects}
                </span>
              </MotifTabControl>
              <MotifTabControl
                id="promptLibraryTab"
                onClick={() => handleTabClick("PromptLibrary")}
              >
                <span
                  className={
                    activeTab === "promptLibraryTab"
                      ? "tab-button tab-active"
                      : "tab-button"
                  }
                >
                  {homeConfig?.tabLabelPromptLibrary}
                </span>
              </MotifTabControl>
            </MotifTabNavigation>
          </div>
          {activeTab === "projectsTab" && (
            <div className="search-section">
              <MotifFormField>
                <MotifSearch
                  aria-label="Search"
                  placeholder="Search"
                  value={searchValue}
                  onChange={filterProjects}
                  searchButtonProps={{ hidden: true }}
                />
                <img src={iconSearch} alt="Search" className="iconSearch" />
              </MotifFormField>
            </div>
          )}
          <div className="body-section spinner-parent">
            {loadingInProgress ? (
              <div className="loaderDiv">
                <div>
                  <img
                    src={iconProgressLoaderCircle}
                    alt="Progress"
                    className="refreshIcon "
                  />
                </div>
                <div className="refreshListText">
                  {homeConfig?.refreshMessage}
                </div>
              </div>
            ) : (
              <Outlet />
            )}
          </div>
        </>
      )}
      {!paansAcknowledge && <PaansAcknowledge></PaansAcknowledge>}
      <div className="genericFooter">
        {location.pathname.indexOf("promptLibrary") === -1 && (
          <div className="footerInner">
            <div className="footerLinks textEllipsis">
              <MotifTextLink
                href={privacyNoticeUrl}
                className="footerLink textEllipsis"
              >
                {homeConfig?.privacyNoticeLabel}{" "}
              </MotifTextLink>
              <span className="privacyNoticeSeparator"> | </span>
              <MotifTextLink
                href={engagementGuidanceUrl}
                className="footerLink textEllipsis"
              >
                {homeConfig?.engagementManagementLabel}
              </MotifTextLink>
            </div>

            {showRefreshButton && (
              <MotifButton
                className="refreshButton"
                variant="secondary"
                id="btnRefreshProjectsList"
                onClick={() => {
                  refreshProjectsList();
                }}
                type="button"
              >
                <img className="refresh-icon" src={iconRefresh} alt="Refresh" />
                {/*
                 */}
                {!paansAcknowledge && homeConfig?.btnRefreshLabel}
                {paansAcknowledge && (
                  <span className="textEllipsis">
                    {homeConfig?.btnRefreshLabel}
                  </span>
                )}
              </MotifButton>
            )}
          </div>
        )}
      </div>
      {showToastMessage && activeTab === "projectsTab" && (
        <div className="toast-div">
          <MotifToast
            onClose={function noRefCheck() {
              setShowToastMessage(false);
            }}
            icons={{
              ...(refreshStatus === "success" && {
                toast: <img src={iconCheckCircle} alt="Success" />,
              }),
            }}
            variant={refreshStatus}
          >
            {refreshStatus === "success"
              ? homeConfig?.refreshSuccessToastMsg
              : homeConfig?.refreshFailureToastMsg}
          </MotifToast>
        </div>
      )}
      {showPaansToastMessage && (
        <div className="toast-div">
          <MotifToast
            onClose={function noRefCheck() {
              setShowPaansToastMessage(false);
            }}
            variant="warning"
            icons={{ toast: <img src={iconWarning} alt="Warning" /> }}
          >
            {homeConfig?.paansMessage}
          </MotifToast>
        </div>
      )}
    </React.Fragment>
  );
}
